import React, { useEffect, useState } from 'react';
import { api, PublicUser } from './api';
import { Icon } from './icons';
import { avatarStyle } from './AccountMenu';

const initial = (s?: string) => (s?.trim()?.[0] || '?').toUpperCase();
const av = (u: PublicUser) => (
  <span className="fx-kudos-av" style={avatarStyle(u)}>{u.avatar_url ? '' : initial(u.name || u.email)}</span>
);

// Read receipts for an announcement message.
export function ReceiptsModal({ messageId, onClose }: { messageId: string; onClose: () => void }) {
  const [data, setData] = useState<{ seen: PublicUser[]; not_seen: PublicUser[]; total: number } | null>(null);
  useEffect(() => {
    api.messageReceipts(messageId).then(setData).catch(() => setData({ seen: [], not_seen: [], total: 0 }));
  }, [messageId]);
  return (
    <div className="fx-modal-backdrop" onClick={onClose}>
      <div className="fx-modal" onClick={(e) => e.stopPropagation()}>
        <div className="fx-login-title">Read receipts</div>
        {!data ? <div className="fx-login-note">Loading…</div> : (
          <>
            <div className="fx-login-note" style={{ textAlign: 'left', marginTop: 0 }}>
              Seen by {data.seen.length} of {data.total}
            </div>
            <div className="fx-receipts">
              <div className="fx-receipts-h">Seen</div>
              {data.seen.length === 0 && <div className="fx-empty">No one yet.</div>}
              {data.seen.map((u) => (
                <div className="fx-kudos-row" key={u.id}>{av(u)}<span className="fx-kudos-name">{u.name || u.email}</span><Icon name="markread" size={14} /></div>
              ))}
              {data.not_seen.length > 0 && <div className="fx-receipts-h">Not yet</div>}
              {data.not_seen.map((u) => (
                <div className="fx-kudos-row" key={u.id}>{av(u)}<span className="fx-kudos-name" style={{ opacity: 0.55 }}>{u.name || u.email}</span></div>
              ))}
            </div>
          </>
        )}
        <div className="fx-modal-actions"><button type="button" className="fx-login-back" onClick={onClose}>Done</button></div>
      </div>
    </div>
  );
}

// Admin: DLP mode + random-coffee (donut).
export function ComplianceModal({ wid, onClose }: { wid: string; onClose: () => void }) {
  const [dlp, setDlpMode] = useState('off');
  const [donut, setDonut] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.getAdminSettings(wid).then((s) => { setDlpMode(s.dlp_mode); setDonut(s.donut_enabled); }).catch(() => {});
  }, [wid]);

  const changeDlp = async (mode: string) => { setDlpMode(mode); try { await api.setDlp(wid, mode); } catch { /* ignore */ } };
  const toggleDonut = async (on: boolean) => { setDonut(on); try { await api.setDonut(wid, on); } catch { /* ignore */ } };
  const runNow = async () => {
    setBusy(true); setNote('');
    try { const r = await api.runDonut(wid); setNote(`Paired ${r.pairs} pair${r.pairs === 1 ? '' : 's'} — intros dropped in their DMs.`); }
    catch (e: any) { setNote(e?.message || 'Could not run'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fx-modal-backdrop" onClick={onClose}>
      <div className="fx-modal" onClick={(e) => e.stopPropagation()}>
        <div className="fx-login-title">Admin &amp; compliance</div>

        <div className="fx-invite-sec">
          <div className="fx-invite-h">Data-loss prevention</div>
          <div className="fx-login-note" style={{ textAlign: 'left', marginTop: 0 }}>
            Scan messages for secrets — API keys, private keys, credit-card numbers.
          </div>
          <div className="fx-seg">
            {(['off', 'warn', 'block'] as const).map((m) => (
              <button key={m} type="button" className={`fx-seg-btn${dlp === m ? ' is-on' : ''}`} onClick={() => changeDlp(m)}>
                {m === 'off' ? 'Off' : m === 'warn' ? 'Warn' : 'Block'}
              </button>
            ))}
          </div>
          <div className="fx-login-note" style={{ marginTop: 6, fontSize: 12 }}>
            {dlp === 'off' ? 'No scanning.' : dlp === 'warn' ? 'Flagged messages send but are marked ⚠︎.' : 'Messages with sensitive data are blocked from sending.'}
          </div>
        </div>

        <div className="fx-invite-sec">
          <div className="fx-invite-h">Random coffee ☕</div>
          <label className="fx-check-row">
            <input type="checkbox" checked={donut} onChange={(e) => toggleDonut(e.target.checked)} />
            <span>Auto-pair members for a weekly 1:1</span>
          </label>
          <button type="button" className="fx-sendbtn fx-login-btn" onClick={runNow} disabled={busy}>{busy ? 'Pairing…' : 'Run a round now'}</button>
          {note && <div className="fx-login-note" style={{ marginTop: 6, color: 'var(--fx-good, #3fbf7f)' }}>{note}</div>}
        </div>

        <div className="fx-modal-actions"><button type="button" className="fx-login-back" onClick={onClose}>Done</button></div>
      </div>
    </div>
  );
}
